import { fmtUsd, fmtNum, fmtPct } from '../utils/fmt';
import { useState, useMemo, useCallback } from 'react';
import { fmtPrice } from '../utils/format';
import InfoTooltip from './InfoTooltip';
import { SourceAttr } from './SourceDot';

// ── Money formatter: 2 decimals for amounts ≥ $1, 4 for sub-dollar amounts ──────
// Megjelenítés: utils/fmt (magyar formátum)
function fmtMoney(n) {
  if (n == null || isNaN(n)) return '—';
  return fmtUsd(n, Math.abs(n) < 1 ? undefined : 2);
}

// ── Persistence ───────────────────────────────────────────────────────────────
function portfolioKey() {
  const email = localStorage.getItem('terminal_email') || '';
  return email ? `local_portfolio_${email}` : 'local_portfolio';
}

function loadHoldings() {
  try {
    const key = portfolioKey();
    const v = JSON.parse(localStorage.getItem(key));
    if (Array.isArray(v)) return v;
    const old = JSON.parse(localStorage.getItem('portfolio') ?? '[]');
    if (old.length > 0) localStorage.setItem(key, JSON.stringify(old));
    return old;
  } catch { return []; }
}

function saveHoldings(h) {
  try { localStorage.setItem(portfolioKey(), JSON.stringify(h)); } catch {}
}

// Merge duplicate holdings (same symbol) into one display row. Display-only —
// localStorage entries stay separate so each position can still be edited/deleted
// individually from the left list. Operates on enriched rows so value/cost/PnL are
// summed and the buy price becomes the quantity-weighted average across entries.
function mergePortfolio(entries) {
  const map = {};
  entries.forEach(e => {
    const key = (e.symbol || '').toUpperCase();
    if (!key) return;
    if (!map[key]) {
      map[key] = { ...e, ids: [e.id], _costSum: e.cost ?? 0, _hasCost: e.cost != null };
    } else {
      const m = map[key];
      m.ids.push(e.id);
      m.quantity += e.quantity;
      m.value    += e.value;
      m._costSum += e.cost ?? 0;
      m._hasCost  = m._hasCost || e.cost != null;
    }
  });
  return Object.values(map).map(m => {
    const cost     = m._hasCost ? m._costSum : null;
    const buyPrice = cost != null && m.quantity > 0 ? cost / m.quantity : null;
    const pnl      = cost != null ? m.value - cost : null;
    const pnlPct   = cost != null && cost > 0 ? ((m.value - cost) / cost) * 100 : null;
    return { ...m, cost, buyPrice, pnl, pnlPct };
  });
}

// ── SVG donut chart ───────────────────────────────────────────────────────────
const DONUT_COLORS = [
  '#f5a623','#00c853','#4a90e2','#ff4d4d','#c678dd','#00e5ff',
  '#69f0ae','#ff9800','#e91e63','#03a9f4',
];

function polarToXY(cx, cy, r, deg) {
  const rad = ((deg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function donutPath(cx, cy, r, startDeg, endDeg) {
  if (endDeg - startDeg >= 359.9) endDeg = startDeg + 359.89;
  const s = polarToXY(cx, cy, r, startDeg);
  const e = polarToXY(cx, cy, r, endDeg);
  const large = endDeg - startDeg > 180 ? 1 : 0;
  return `M ${s.x} ${s.y} A ${r} ${r} 0 ${large} 1 ${e.x} ${e.y}`;
}

function DonutChart({ slices }) {
  const CX = 80, CY = 80, R = 62, THICK = 18;
  let angle = 0;
  return (
    <svg width={CX * 2} height={CY * 2} viewBox={`0 0 ${CX * 2} ${CY * 2}`} className="donut-svg">
      {slices.map((s, i) => {
        const deg = (s.pct / 100) * 360;
        const start = angle, end = angle + deg;
        angle += deg;
        const color = DONUT_COLORS[i % DONUT_COLORS.length];
        return (
          <path
            key={s.id}
            d={donutPath(CX, CY, R, start, end)}
            fill="none"
            stroke={color}
            strokeWidth={THICK}
            opacity={0.9}
          />
        );
      })}
      <circle cx={CX} cy={CY} r={R - THICK / 2 - 2} fill="var(--surface)" />
      <text x={CX} y={CY - 6} textAnchor="middle" fill="var(--text)" fontSize={11} fontWeight="700">
        Portfólió
      </text>
      <text x={CX} y={CY + 10} textAnchor="middle" fill="var(--text3)" fontSize={9}>
        allokáció
      </text>
    </svg>
  );
}

// ── Add Holding form ──────────────────────────────────────────────────────────
function AddHoldingForm({ coins, onAdd }) {
  const [coinId,   setCoinId]   = useState(coins[0]?.id ?? '');
  const [qty,      setQty]      = useState('');
  const [buyPrice, setBuyPrice] = useState('');

  function handleAdd() {
    const q = parseFloat(qty);
    if (!coinId || !q || q <= 0) return;
    const coin = coins.find(c => c.id === coinId);
    if (!coin) return;
    onAdd({
      id: `${coinId}-${Date.now()}`,
      coinId,
      symbol:   coin.symbol.toUpperCase(),
      name:     coin.name,
      image:    coin.image,
      quantity: q,
      buyPrice: parseFloat(buyPrice) || null,
    });
    setQty('');
    setBuyPrice('');
  }

  return (
    <div className="port-add-form">
      <div className="port-field-label">Coin</div>
      <select className="port-select" value={coinId} onChange={e => setCoinId(e.target.value)}>
        {coins.map(c => (
          <option key={c.id} value={c.id}>{c.symbol.toUpperCase()} — {c.name}</option>
        ))}
      </select>

      <div className="port-field-label">Mennyiség</div>
      <input className="port-input" type="number" placeholder="0.00" min="0"
        value={qty} onChange={e => setQty(e.target.value)} />

      <div className="port-field-label">
        Átl. Vásárlási Ár ($) <span className="port-optional">(opcionális)</span>
      </div>
      <input className="port-input" type="number" placeholder="pl. 77000" min="0"
        value={buyPrice} onChange={e => setBuyPrice(e.target.value)} />

      <button className="port-add-btn" onClick={handleAdd}
        disabled={!qty || parseFloat(qty) <= 0}>
        + Pozíció Hozzáadása
      </button>

      <div className="port-form-note">
        💡 Az átlagár megadása szükséges a P&L számításhoz
      </div>
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────
export default function PortfolioTab({ coins, scannerData }) {
  const allCoins = useMemo(() => {
    const base = scannerData ?? coins;
    const seen = new Set();
    return base.filter(c => {
      if (seen.has(c.id)) return false;
      seen.add(c.id);
      return true;
    });
  }, [coins, scannerData]);

  const [holdings, setHoldings] = useState(loadHoldings);
  const [editId,   setEditId]   = useState(null);
  const [editData, setEditData] = useState({ qty: '', buyPrice: '' });

  const addHolding = useCallback(h => {
    setHoldings(prev => {
      const next = [...prev, h];
      saveHoldings(next);
      return next;
    });
  }, []);

  const removeHolding = useCallback(id => {
    setHoldings(prev => {
      const next = prev.filter(h => h.id !== id);
      saveHoldings(next);
      return next;
    });
    setEditId(curr => curr === id ? null : curr);
  }, []);

  const startEdit = useCallback(h => {
    setEditId(h.id);
    setEditData({ qty: String(h.quantity), buyPrice: h.buyPrice != null ? String(h.buyPrice) : '' });
  }, []);

  const cancelEdit = useCallback(() => setEditId(null), []);

  const saveEdit = useCallback(() => {
    const q = parseFloat(editData.qty);
    if (!q || q <= 0) return;
    setHoldings(prev => {
      const next = prev.map(h => {
        if (h.id !== editId) return h;
        return { ...h, quantity: q, buyPrice: parseFloat(editData.buyPrice) || null };
      });
      saveHoldings(next);
      return next;
    });
    setEditId(null);
  }, [editId, editData]);

  // Enrich holdings with live prices
  const enriched = useMemo(() => {
    return holdings.map(h => {
      const live   = allCoins.find(c => c.id === h.coinId);
      const price  = live?.current_price ?? 0;
      const value  = h.quantity * price;
      const cost   = h.buyPrice ? h.quantity * h.buyPrice : null;
      const pnl    = cost != null ? value - cost : null;
      const pnlPct = cost != null && cost > 0 ? ((value - cost) / cost) * 100 : null;
      return { ...h, price, value, cost, pnl, pnlPct, change24h: live?.price_change_percentage_24h ?? null };
    });
  }, [holdings, allCoins]);

  // Duplicate-symbol rows merged for display (chart + table). Totals below stay
  // on `enriched` since a sum is identical either way.
  const merged = useMemo(() => mergePortfolio(enriched), [enriched]);

  const totalValue = useMemo(() => enriched.reduce((s, h) => s + h.value, 0), [enriched]);

  const totalCost = useMemo(() => {
    const costed = enriched.filter(h => h.cost != null);
    return costed.length ? costed.reduce((s, h) => s + h.cost, 0) : null;
  }, [enriched]);

  const totalPnl    = totalCost != null ? totalValue - totalCost : null;
  const totalPnlPct = totalCost != null && totalCost > 0 ? ((totalValue - totalCost) / totalCost) * 100 : null;

  const todayPnl = useMemo(() => enriched.reduce((s, h) => {
    if (h.change24h == null) return s;
    return s + h.value - h.value / (1 + h.change24h / 100);
  }, 0), [enriched]);

  const donutSlices = useMemo(() => {
    if (!totalValue) return [];
    return merged
      .filter(h => h.value > 0)
      .map(h => ({ id: h.id, symbol: h.symbol, pct: (h.value / totalValue) * 100 }))
      .sort((a, b) => b.pct - a.pct);
  }, [merged, totalValue]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="portfolio-tab">

      {/* Left: add form + positions list */}
      <div className="port-sidebar">
        <div className="port-sidebar-title">POZÍCIÓ HOZZÁADÁSA</div>
        <AddHoldingForm coins={allCoins} onAdd={addHolding} />

        {holdings.length > 0 && (
          <>
            <div className="port-sidebar-title" style={{ marginTop: 20 }}>POZÍCIÓK</div>
            <div className="port-holdings-list">
              {enriched.map((h, i) => (
                <div key={h.id}>
                  <div className="port-holding-item">
                    <div className="port-holding-dot"
                      style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                    <img src={h.image} alt={h.symbol} className="port-holding-icon"
                      onError={e => { e.target.style.display = 'none'; }} />
                    <div className="port-holding-info">
                      <div className="port-holding-sym">{h.symbol}</div>
                      <div className="port-holding-qty">{h.quantity} coins</div>
                    </div>
                    <div className="port-holding-val">{fmtMoney(h.value)}</div>
                    <button className="port-edit-btn" onClick={() => editId === h.id ? cancelEdit() : startEdit(h)}
                      title="Szerkesztés">✏️</button>
                    <button className="port-remove-btn" onClick={() => removeHolding(h.id)}
                      title="Törlés">✕</button>
                  </div>

                  {editId === h.id && (
                    <div className="port-inline-form">
                      <div className="port-inline-row">
                        <label className="port-inline-label">Mennyiség</label>
                        <input
                          className="port-input port-inline-input"
                          type="number" min="0"
                          value={editData.qty}
                          onChange={e => setEditData(d => ({ ...d, qty: e.target.value }))}
                        />
                      </div>
                      <div className="port-inline-row">
                        <label className="port-inline-label">Átl. Ár ($)</label>
                        <input
                          className="port-input port-inline-input"
                          type="number" min="0" placeholder="pl. 77000"
                          value={editData.buyPrice}
                          onChange={e => setEditData(d => ({ ...d, buyPrice: e.target.value }))}
                        />
                      </div>
                      <div className="port-inline-actions">
                        <button className="port-inline-save" onClick={saveEdit}>💾 Mentés</button>
                        <button className="port-inline-cancel" onClick={cancelEdit}>Mégse</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="port-sidebar-total">
              Összes pozíció értéke: <strong>{fmtMoney(totalValue)}</strong>
            </div>
          </>
        )}
      </div>

      {/* Right: main panel */}
      <div className="port-main">
        {holdings.length === 0 ? (
          <div className="port-empty">
            <div className="port-empty-icon">◈</div>
            <div>Add hozzá az első pozíciódat a kezdéshez.</div>
            <div className="port-empty-sub">Az árak 60 másodpercenként frissülnek a CoinGecko-ról.</div>
          </div>
        ) : (
          <>
            {/* Summary cards — always 4 */}
            <div className="port-summary-grid">
              <div className="port-card port-card-main">
                <div className="port-card-label">TELJES ÉRTÉK <InfoTooltip text="Az összes befektetésed megoszlása egy pillantásra. Ha ugyanabból a coinból többször vettél különböző árakon, azokat összevonja és az átlagárat mutatja — így látod valódi kitettségedet coinonként." /></div>
                <div className="port-card-big">{fmtMoney(totalValue)}</div>
                {todayPnl !== 0 && (
                  <div className={`port-card-sub ${todayPnl >= 0 ? 'positive' : 'negative'}`}>
                    {todayPnl >= 0 ? '+' : ''}{fmtMoney(todayPnl)} ma
                  </div>
                )}
              </div>

              <div className={`port-card ${totalPnl == null ? '' : totalPnl >= 0 ? 'port-card-pos' : 'port-card-neg'}`}>
                <div className="port-card-label">NEM REALIZÁLT P&L</div>
                {totalPnl != null ? (
                  <>
                    <div className={`port-card-big ${totalPnl >= 0 ? 'positive' : 'negative'}`}>
                      {totalPnl >= 0 ? '+' : ''}{fmtMoney(totalPnl)}
                    </div>
                    {totalPnlPct != null && (
                      <div className={`port-card-sub ${totalPnlPct >= 0 ? 'positive' : 'negative'}`}>
                        {fmtPct(totalPnlPct, 2)}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="port-card-big muted">—</div>
                    <div className="port-card-sub" style={{ color: 'var(--text3)' }}>add meg az átlagárat</div>
                  </>
                )}
              </div>

              <div className="port-card">
                <div className="port-card-label">BEKERÜLÉSI ÉRTÉK</div>
                {totalCost != null ? (
                  <>
                    <div className="port-card-big">{fmtMoney(totalCost)}</div>
                    <div className="port-card-sub" style={{ color: 'var(--text3)' }}>átlagáras pozíciók</div>
                  </>
                ) : (
                  <>
                    <div className="port-card-big muted">—</div>
                    <div className="port-card-sub" style={{ color: 'var(--text3)' }}>add meg az átlagárat</div>
                  </>
                )}
              </div>

              <div className={`port-card ${todayPnl >= 0 ? 'port-card-pos' : 'port-card-neg'}`}>
                <div className="port-card-label">MAI P&L</div>
                <div className={`port-card-big ${todayPnl >= 0 ? 'positive' : 'negative'}`}>
                  {todayPnl >= 0 ? '+' : ''}{fmtMoney(todayPnl)}
                </div>
                <div className="port-card-sub" style={{ color: 'var(--text3)' }}>24h változás</div>
              </div>
            </div>

            {/* Donut + allocation */}
            <div className="port-visual-row">
              <div className="port-donut-wrap">
                {donutSlices.length > 0 && <DonutChart slices={donutSlices} />}
              </div>
              <div className="port-alloc-list">
                {donutSlices.map((s, i) => (
                  <div key={s.id} className="port-alloc-row">
                    <div className="port-alloc-dot"
                      style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                    <span className="port-alloc-sym">{s.symbol}</span>
                    <div className="port-alloc-bar-track">
                      <div className="port-alloc-bar-fill"
                        style={{ width: `${s.pct}%`, background: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                    </div>
                    <span className="port-alloc-pct">{fmtNum(s.pct, 1)}%</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Holdings table */}
            <div className="port-table-wrap">
              <table className="port-table">
                <thead>
                  <tr>
                    <th>Coin</th>
                    <th className="right">Ár</th>
                    <th className="right">24h %</th>
                    <th className="right">Mennyiség</th>
                    <th className="right">Érték</th>
                    <th className="right">Átl. Ár</th>
                    <th className="right">Bekerülési Érték</th>
                    <th className="right">Nyereség/Veszteség</th>
                    <th className="right">Hozam %</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {merged.map((h, i) => (
                    <tr key={h.id} className="port-table-row">
                      <td>
                        <div className="port-asset-cell">
                          <div className="port-dot"
                            style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                          <img src={h.image} alt={h.symbol} className="port-table-icon"
                            onError={e => { e.target.style.display = 'none'; }} />
                          <div>
                            <div className="port-table-sym">{h.symbol}</div>
                            <div className="port-table-name">{h.name}</div>
                          </div>
                        </div>
                      </td>
                      <td className="right">{fmtPrice(h.price)}</td>
                      <td className={`right ${(h.change24h ?? 0) >= 0 ? 'positive' : 'negative'}`}>
                        {h.change24h != null ? `${fmtPct(h.change24h, 2)}` : '—'}
                      </td>
                      <td className="right muted">{h.quantity.toLocaleString()}</td>
                      <td className="right"><strong>{fmtMoney(h.value)}</strong></td>
                      <td className="right muted">
                        {h.buyPrice != null ? fmtPrice(h.buyPrice) : '—'}
                      </td>
                      <td className="right muted">
                        {h.cost != null ? fmtMoney(h.cost) : '—'}
                      </td>
                      <td className={`right ${h.pnl == null ? 'muted' : h.pnl >= 0 ? 'positive' : 'negative'}`}>
                        {h.pnl != null ? `${h.pnl >= 0 ? '+' : ''}${fmtMoney(h.pnl)}` : '—'}
                      </td>
                      <td className={`right ${h.pnlPct == null ? 'muted' : h.pnlPct >= 0 ? 'positive' : 'negative'}`}>
                        {h.pnlPct != null ? `${fmtPct(h.pnlPct, 2)}` : '—'}
                      </td>
                      <td>
                        <button className="port-remove-btn" onClick={() => h.ids.forEach(id => removeHolding(id))} title="Törlés">✕</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Today's P&L row */}
              <div className={`port-day-pnl-row ${todayPnl >= 0 ? 'positive' : 'negative'}`}>
                <span className="port-day-pnl-label">Mai változás:</span>
                <span className="port-day-pnl-val">
                  {todayPnl >= 0 ? '+' : ''}{fmtMoney(todayPnl)}
                </span>
              </div>
            </div>
            <SourceAttr cg />
          </>
        )}
      </div>
    </div>
  );
}
